import { TableRow, TableCell, Avatar, Typography, Chip, IconButton, Tooltip, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PieChartIcon from '@mui/icons-material/PieChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { ItemType, EventDetails, InventoryHistory } from '../schema';

interface ConsumptionRowProps {
    itemType: ItemType;
    event: EventDetails;
    itemLocation?: { quantity: number } | null;
    consumptionHistory?: InventoryHistory | null;
    totalTaken: number; // Total quantity taken to event (from packing list or itemLocation)
    onRecord: () => void;
    onEdit: () => void;
}

type ConsumptionStatus = 'not-recorded' | 'partially-consumed' | 'fully-consumed' | 'over-consumed';

function getConsumptionStatus(used: number, taken: number): ConsumptionStatus {
    if (used === 0) return 'not-recorded';
    if (used < taken) return 'partially-consumed';
    if (used === taken) return 'fully-consumed';
    return 'over-consumed';
}

function StatusIndicator({ status, used, taken }: { status: ConsumptionStatus; used: number; taken: number }) {
    const statusConfig = {
        'not-recorded': {
            icon: HelpOutlineIcon,
            color: 'default' as const,
            label: 'Not recorded',
        },
        'partially-consumed': {
            icon: PieChartIcon,
            color: 'info' as const,
            label: `${Math.round((used / taken) * 100)}% used`,
        },
        'fully-consumed': {
            icon: CheckCircleIcon,
            color: 'success' as const,
            label: 'Fully consumed',
        },
        'over-consumed': {
            icon: WarningIcon,
            color: 'error' as const,
            label: `Over by ${used - taken}`,
        },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
        <Chip
            icon={<Icon />}
            label={config.label}
            color={config.color}
            size="small"
            variant={status === 'not-recorded' ? 'outlined' : 'filled'}
        />
    );
}

export default function ConsumptionRow({
    itemType,
    event: _event,
    itemLocation: _itemLocation,
    consumptionHistory,
    totalTaken,
    onRecord,
    onEdit,
}: ConsumptionRowProps) {
    const used = consumptionHistory ? Math.abs(consumptionHistory.quantityChange || 0) : 0;
    const remaining = totalTaken - used;
    const status = getConsumptionStatus(used, totalTaken);
    const hasHistory = !!consumptionHistory;

    return (
        <TableRow>
            {/* Photo */}
            <TableCell>
                {itemType.photoUrl ? (
                    <Avatar src={itemType.photoUrl} variant="rounded" sx={{ width: 32, height: 32 }} />
                ) : (
                    <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                        <LocalGasStationIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                )}
            </TableCell>

            {/* Name with Category */}
            <TableCell>
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {itemType.name}
                    </Typography>
                    {itemType.category && (
                        <Chip
                            label={itemType.category.name}
                            size="small"
                            sx={{ mt: 0.5 }}
                            color={itemType.category.color ? 'primary' : 'default'}
                            variant="outlined"
                        />
                    )}
                </Box>
            </TableCell>

            {/* Taken */}
            <TableCell>
                <Typography variant="body2">{totalTaken} taken</Typography>
            </TableCell>

            {/* Used */}
            <TableCell>
                <Typography variant="body2">{hasHistory ? `${used} used` : '—'}</Typography>
            </TableCell>

            {/* Remaining */}
            <TableCell>
                <Typography variant="body2">{hasHistory ? `${remaining} remaining` : `${totalTaken} remaining`}</Typography>
            </TableCell>

            {/* Status */}
            <TableCell>
                <StatusIndicator status={status} used={used} taken={totalTaken} />
            </TableCell>

            {/* Actions */}
            <TableCell>
                {hasHistory ? (
                    <Tooltip title="Edit consumption">
                        <IconButton size="small" onClick={onEdit}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title="Consume">
                        <IconButton size="small" onClick={onRecord} color="primary">
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        </TableRow>
    );
}
